import { useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as appActions from 'Actions/app';
import { MainAppReducer } from 'Types';
import { useEffectiveGrading } from 'Utils/playAlong/effectiveGrading';

/**
 * Which Repertoire page (Home or Browse) a player whose notes are not graded
 * lands on.
 *
 * Graded players keep today's behaviour: Repertoire remembers the page they
 * were on, and Home is the first page.
 *
 * Ungraded players have no progress to recommend from, so Home shows only the
 * pieces they played recently, and Browse is where Repertoire starts:
 * - Repertoire opens on Browse.
 * - Opening a piece from Home or Browse, or from outside Repertoire (the
 *   roadmap, a lesson), makes Home the page Repertoire returns to, so the
 *   piece is there under "Recently Played" when the player comes back. A
 *   piece opened from Repertoire's Create or Library tab returns to that tab.
 * - Leaving for another section of the app (from the list or from a piece)
 *   resets Home to Browse. Create and Library are left as they are.
 * - Home with nothing recently played shows Browse (see repertoirePageToShow),
 *   and stays on Browse for the rest of that visit (see Repertoire).
 */

export const REPERTOIRE_LIST_PATH = '/repertoire';
export const REPERTOIRE_PIECE_PATH = '/repertoire-play';

/** Tabs a piece opened from them returns to, rather than Home. */
const TABS_A_PIECE_RETURNS_TO = new Set(['Create', 'Library']);

export interface RepertoireRouteChange {
  graded: boolean;
  /** The path the player was on before this one (null on the first route). */
  previousPath: string | null;
  path: string;
  /** The page Repertoire is set to now (`repPageSelected`). */
  currentPage: string;
}

/** The page Repertoire should be set to once the player is on `path`. */
export const repertoirePageAfterRouteChange = ({
  graded,
  previousPath,
  path,
  currentPage,
}: RepertoireRouteChange): string => {
  if (graded) return currentPage;
  if (path === REPERTOIRE_PIECE_PATH) {
    // Create and Library live on the Repertoire page: only a piece opened from
    // there returns to them. A piece opened anywhere else returns to Home.
    const openedFromRepertoireTab =
      previousPath === REPERTOIRE_LIST_PATH && TABS_A_PIECE_RETURNS_TO.has(currentPage);
    return openedFromRepertoireTab ? currentPage : 'Home';
  }
  const returningFromPiece =
    path === REPERTOIRE_LIST_PATH && previousPath === REPERTOIRE_PIECE_PATH;
  if (returningFromPiece) return currentPage;
  return currentPage === 'Home' ? 'Browse' : currentPage;
};

/** The page to render: an ungraded Home with nothing recently played shows Browse. */
export const repertoirePageToShow = (
  selectedPage: string,
  graded: boolean,
  recentlyPlayedCount: number,
): string =>
  !graded && selectedPage === 'Home' && recentlyPlayedCount === 0 ? 'Browse' : selectedPage;

const selectRepPageSelected = (state: MainAppReducer) => state.mainAppReducer.repPageSelected;

/**
 * Keeps an ungraded player's Repertoire page in step with where they go in the
 * app. Mounted once, above the routes, so it sees every route change, and the
 * page is already right when Repertoire renders. Runs when the path changes or
 * grading changes, never when the player picks a page themselves.
 */
export function useRepertoireLandingPage(pathname: string): void {
  const dispatch = useDispatch();
  const graded = useEffectiveGrading();
  const currentPage = useSelector(selectRepPageSelected);
  const currentPageRef = useRef(currentPage);
  currentPageRef.current = currentPage;
  const routeRef = useRef<{ previousPath: string | null; path: string | null }>({
    previousPath: null,
    path: null,
  });

  useEffect(() => {
    if (routeRef.current.path !== pathname) {
      routeRef.current = { previousPath: routeRef.current.path, path: pathname };
    }
    const nextPage = repertoirePageAfterRouteChange({
      graded,
      previousPath: routeRef.current.previousPath,
      path: pathname,
      currentPage: currentPageRef.current,
    });
    if (nextPage !== currentPageRef.current) dispatch(appActions.setRepPageSelected(nextPage));
  }, [pathname, graded, dispatch]);
}
